import {
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { PatientCaseContent } from '@llx/common';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { FiEye, FiMoreVertical } from 'react-icons/fi';
import { MdOutlineAutorenew } from 'react-icons/md';

import {
  PatientCaseFieldsFragment,
  PatientCaseRelationCaseClusterFragment,
  PatientCaseRelationClientFragment,
} from '@/@generated/codegen/API';
import CaseClusterTag from '@/components/CaseClusterTag/CaseClusterTag';
import ClientTag from '@/components/ClientTag/ClientTag';

export type AdminCaseListRowType = PatientCaseFieldsFragment &
  PatientCaseRelationCaseClusterFragment &
  PatientCaseRelationClientFragment & {
    numberOfSlides: number;
    patientCaseContent: PatientCaseContent;
  };

const columnHelper = createColumnHelper<AdminCaseListRowType>();

type Args = {
  t: TFunction<'translation', undefined, 'translation'>;
  onViewCaseClick: (data: AdminCaseListRowType) => void;
  onPreviewLinkClick: (data: AdminCaseListRowType) => void;
};

const getColumns = ({ t, onViewCaseClick, onPreviewLinkClick }: Args) => {
  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (props) => {
        const { name, patientCaseContent, legacyCaseId } = props.row.original;
        return (
          <Tooltip label={name}>
            <div>
              <Text>{name}</Text>
              <Text fontSize={12} fontStyle="italic">
                {patientCaseContent.displayName}
              </Text>
              <Text fontSize={12} fontStyle="italic">
                ID: {legacyCaseId}
              </Text>
            </div>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('client', {
      header: 'Client',
      enableSorting: false,
      cell: (props) => {
        const clientName = props.row.original.client?.name;
        if (!clientName) {
          return (
            <Tag ml={2} colorScheme="red">
              NOT FOUND
            </Tag>
          );
        }
        return <ClientTag client={clientName} />;
      },
    }),
    columnHelper.accessor('creationDate', {
      header: 'Creation Date',
      cell: (info) => {
        const value = new Date(info.getValue());
        return value.toLocaleDateString('en-GB'); // DD/MM/YYYY
      },
    }),

    columnHelper.display({
      id: 'numberOfSlides',
      header: '# of Slides',
      enableSorting: false,
      cell: (props) => <Text>{props.row.original.numberOfSlides}</Text>,
    }),
    columnHelper.accessor('purchaseOrderNbr', {
      header: 'PO',
    }),

    columnHelper.accessor('caseClusters', {
      header: 'Clusters',
      enableSorting: false,
      cell: (props) => {
        const elems = props.getValue().edges.map((el) => el.node);
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {elems.map((elem) => (
              <CaseClusterTag key={elem.id} name={elem.name} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (props) => {
        const data = props.row.original;
        return (
          <HStack>
            <Button onClick={() => onViewCaseClick(data)} variant="secondary">
              <FiEye />
              <Text ml="5px">{t('admin.cases.table.viewCase.button')}</Text>
            </Button>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<FiMoreVertical />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  icon={<MdOutlineAutorenew />}
                  onClick={() => {
                    onPreviewLinkClick(data);
                  }}
                >
                  {t('admin.cases.table.actions.generatePreview.button')}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        );
      },
      minSize: 200,
    }),
  ];

  return columns;
};

export default getColumns;
